<template>
  <b-row class="my-3">
    <b-col cols="12">
      <h4><b>Participants Filter</b></h4>
      <p>Select participants by department and/or location</p>
      <advanced-form :form="formFields" v-bind="form" v-model="form" :hideFormControl="true" />
    </b-col>
    <b-col cols="12">
      <b-button @click="updateFilters">Update Filters</b-button>
    </b-col>
  </b-row>
</template>

<script>
import CampaignAnalyticsFilterQuery from '@/graphql/queries/CampaignAnalyticsFilter.gql';
import AdvancedForm from '@src/components/AdvancedForm';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'CampaignAnalyticsFilter',
  props: {
    value: {
      type: Object,
    }
  },
  components: {
    AdvancedForm
  },
  data: () => ({
    form: {},
  }),
  apollo: {
    api: {
      query: CampaignAnalyticsFilterQuery,
      update({ company }) {
        return company;
      },
      fetchPolicy: 'network-only'
    },
  },
  created() {
    // Set segment form
    const { value } = this;
    if (!value) return;
    const { AND } = value;
    if (!Array.isArray(AND) || !AND.length) return;
    this.form = AND.reduce((r, { NOT, OR }) => {
      if (NOT && NOT.length) {
        const { id_in: idIn } = NOT[0];
        this.notIDs = idIn;
        return r;
      } else if (Array.isArray(OR) && OR.length) {
        if (OR[0].location) {
          r.locations = OR.map(({ location }) => location).filter(el => el);
          return r;
        } else if (OR[0].department) {
          r.departments = OR.map(({ department }) => department).filter(el => el);
          return r;
        } else if (OR[0].organization) {
          r.organizations = OR.map(({ organization }) => organization).filter(el => el);
          return r;
        } else if (OR[0].division) {
          r.divisions = OR.map(({ division }) => division).filter(el => el);
          return r;
        } else if (OR[0].title) {
          r.titles = OR.map(({ title }) => title).filter(el => el);
          return r;
        } else if (OR[0].countryCode) {
          r.countryCodes = OR.map(({ countryCode }) => countryCode).filter(el => el);
          return r;
        }
      }
    }, {});
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isRecruiter', 'isRecruiterAdmin', 'isProd', 'isEXPD8', 'isInMobi' ]),
    segment() {
      const { form } = this;
      const { departments, locations, organizations, divisions, titles, countryCodes } = form || {};

      if ((!Array.isArray(departments) || !departments.length) &&
        (!Array.isArray(locations) || !locations.length) &&
        (!Array.isArray(organizations) || !organizations.length) &&
        (!Array.isArray(divisions) || !divisions.length) &&
        (!Array.isArray(titles) || !titles.length) &&
        (!Array.isArray(countryCodes) || !countryCodes.length)) {
        return null;
      }

      const AND = [];
      if (Array.isArray(departments) && departments.length) {
        AND.push({ OR: departments.map(value => ({ department: value })) });
      }
      if (Array.isArray(locations) && locations.length) {
        AND.push({ OR: locations.map(value => ({ location: value })) });
      }
      if (Array.isArray(organizations) && organizations.length) {
        AND.push({ OR: organizations.map(value => ({ organization: value })) });
      }
      if (Array.isArray(divisions) && divisions.length) {
        AND.push({ OR: divisions.map(value => ({ division: value })) });
      }
      if (Array.isArray(titles) && titles.length) {
        AND.push({ OR: titles.map(value => ({ title: value })) });
      }
      if (Array.isArray(countryCodes) && countryCodes.length) {
        AND.push({ OR: countryCodes.map(value => ({ countryCode: value })) });
      }

      return { AND };
    },
    returnSegment() {
      const { segment } = this;
      if (!segment) return null;
      const result = { ...segment };
      return result;
    },
    formFields() {
      const { scimUserAggs } = this.api || {};
      const { departments, locations, organizations, divisions, titles, countryCodes } = scimUserAggs || {};
      return {
        ...(!this.isInMobi && !this.isERM && Array.isArray(departments) && departments.length ? {
          departments: {
            label: 'Departments',
            type: 'MULTISELECT',
            choices: departments.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(!this.isInMobi && !this.isERM && Array.isArray(locations) && locations.length ? {
          locations: {
            label: 'Locations',
            type: 'MULTISELECT',
            choices: locations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isEXPD8 && Array.isArray(organizations) && organizations.length ? {
          organizations: {
            label: 'Regions',
            type: 'MULTISELECT',
            choices: organizations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isEXPD8 && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Districts',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isInMobi && Array.isArray(departments) && departments.length ? {
          departments: {
            label: 'Cost Owners',
            type: 'MULTISELECT',
            choices: departments.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isInMobi && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Functions',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isInMobi && Array.isArray(locations) && locations.length ? {
          locations: {
            label: 'Cities',
            type: 'MULTISELECT',
            choices: locations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...((this.isChalhoub || this.isInMobi || this.isERM) && Array.isArray(countryCodes) && countryCodes.length ? {
          countryCodes: {
            label: 'Countries',
            type: 'MULTISELECT',
            choices: countryCodes.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(locations) && locations.length ? {
          locations: {
            label: 'Locations',
            type: 'MULTISELECT',
            choices: locations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(organizations) && organizations.length ? {
          organizations: {
            label: 'Organizations',
            type: 'MULTISELECT',
            choices: organizations.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(departments) && departments.length ? {
          departments: {
            label: 'Departments',
            type: 'MULTISELECT',
            choices: departments.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(divisions) && divisions.length ? {
          divisions: {
            label: 'Managers',
            type: 'MULTISELECT',
            choices: divisions.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
        ...(this.isERM && Array.isArray(titles) && titles.length ? {
          titles: {
            label: 'Titles',
            type: 'MULTISELECT',
            choices: titles.reduce((r, { name, count }) => ({ ...r, [name]: `${name} (${count})` }), {}),
          },
        } : {}),
      };
    }
  },
  methods: {
    updateFilters() {
      const { returnSegment } = this;
      console.log(returnSegment);
      this.$emit('input', returnSegment);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/CampaignAnalyticsFilterUser.vue